import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  fetchInvoiceSearch,
  clearSearchResults,
  clearError,
  clearMessage,
  setCurrentPage,
  setPageSize,
  setSearchQuery,
} from '../features/invoice-search/invoice-search.slice';
import type { InvoiceSearchParams } from '../api/invoice-search';

/**
 * Custom hook for managing invoice search with Redux
 * @returns Object containing search state and action methods
 */
export const useInvoiceSearch = () => {
  const dispatch = useDispatch();
  const {
    searchResults,
    loading,
    error,
    message,
    pagination,
    filters,
  } = useSelector((state: RootState) => state.invoiceSearch);

  const performSearch = async (params: InvoiceSearchParams) => {
    try {
      // Sanitize search parameters
      const sanitizedParams: InvoiceSearchParams = {
        PageNumber: Math.max(1, Math.floor(Number(params.PageNumber))),
        PageSize: Math.max(1, Math.floor(Number(params.PageSize))),
        ...(params.searchQuery && { searchQuery: String(params.searchQuery).trim().slice(0, 500) }),
      };
      await dispatch(fetchInvoiceSearch(sanitizedParams) as any);
    } catch (err) {
      console.error('Search dispatch error:', err);
    }
  };

  const goToNextPage = () => {
    if (pagination.hasNextPage) {
      const newPage = pagination.currentPage + 1;
      dispatch(setCurrentPage(newPage));
      // Re-perform search with new page
      performSearch({
        PageNumber: newPage,
        PageSize: pagination.pageSize,
        searchQuery: filters.searchQuery,
      });
    }
  };

  const goToPreviousPage = () => {
    if (pagination.hasPreviousPage) {
      const newPage = pagination.currentPage - 1;
      dispatch(setCurrentPage(newPage));
      // Re-perform search with new page
      performSearch({
        PageNumber: newPage,
        PageSize: pagination.pageSize,
        searchQuery: filters.searchQuery,
      });
    }
  };

  const updateSearchQuery = (query: string) => {
    // Sanitize search query: trim whitespace and limit length
    const sanitizedQuery = String(query).trim().slice(0, 500);
    dispatch(setSearchQuery(sanitizedQuery));
  };

  const updatePageSize = (size: number) => {
    dispatch(setPageSize(size));
  };

  const clear = () => {
    dispatch(clearSearchResults());
  };

  const clearErrorMessage = () => {
    dispatch(clearError());
  };

  const clearSuccessMessage = () => {
    dispatch(clearMessage());
  };

  return {
    // State - with null-safe defaults
    searchResults: searchResults || [],
    loading,
    error,
    message,
    pagination,
    filters,
    // Actions
    performSearch,
    goToNextPage,
    goToPreviousPage,
    updateSearchQuery,
    updatePageSize,
    clear,
    clearErrorMessage,
    clearSuccessMessage,
  };
};
